import { lazy, useState } from "react"

import { Link } from "react-router-dom"

import Heading from "$/components/Heading/Heading"
import { CRONOFY_WIDGET } from "$/constants"
import { axiosClient } from "$/http"
import { getErrorMessages } from "$/utils/get-error-messages"

import {
    Button,
    ETypographyAlignment,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Typography
} from "@/3514/components"
import { type TUserState, useUser } from "@/3514/hooks"
import Urls from "@/api/urls"
// WISER-5808
import "@/assets/coach.scss"
import { useAvailabilityConfirmRules } from "@/entities/coach-calendar/api/use-confirm"
import { useCoachCalendarOrderNavigation } from "@/entities/coach-calendar/hooks"
import { SimpleLayout } from "@/layouts"
import DateWidget from "@/shared/DateWidget"
// WISER-5808
import { WarningMessage } from "@/shared/WarningMessage"
import { FullSpinner } from "@/shared/spinner"

import { CoachCalendarStepUrls } from "./constants"
import { StepsNavigator } from "./ui/StepNavigator"

const LazyCronofyWidget = lazy(() => import("$/components/CronofyWidgets/CronofyWidget"))

type Props = {
    order?: number
}

const ConfirmAvailabilityStep = ({ order }: Props) => {
    const { refetch: refetchUser }: TUserState = useUser()

    const { navigateByOrder } = useCoachCalendarOrderNavigation(order)
    const { data, isLoading, error: apiError } = useAvailabilityConfirmRules()
    const [error, setError] = useState(null)

    const onConfirmClick = async () => {
        try {
            await axiosClient.post(Urls.confirmSetupCalendar())
            refetchUser()
            navigateByOrder()
        } catch (error) {
            setError(getErrorMessages(error))
        }
    }

    const config = {
        ...(data ?? {}),
        config: { ...data?.config, interval: 15 },
        callback: () => {}
    }

    return (
        <SimpleLayout
            header={<DateWidget className="coach-date-title" />}
            headerVariant="gray"
            showBackBtn
            showProfilePhoto
            className="coach-confirm-availability-layout"
        >
            <div className="coach-calendar-wrapper mx-auto pb-5">
                <div className="d-flex justify-content-center mt-5">
                    <StepsNavigator activeStep={CoachCalendarStepUrls.AVAILABILITY_CONFIRM_STEP} />
                </div>
                <Heading textAlign="center" tag="h6" fontSize={16} className="mb-[40px] mt-5 text-accentOld">
                    Step {order}
                </Heading>
                <Typography
                    text="Confirm Your Availability"
                    className="mb-2"
                    alignment={ETypographyAlignment.Center}
                    tag={ETypographyTag.Heading1}
                    weight={ETypographyWeight.Medium}
                    size={ETypographySize.Heading1}
                    family={ETypographyFamily.DmSerif}
                />

                <p className="mb-0 color-gray fs-14 text-center">
                    <span className="font-extrabold">You must confirm</span> or else your schedule will not be saved.
                    Check to make sure that everything looks right.
                </p>

                <p className="mb-1 color-gray fs-14 text-center">
                    This is the availability that will be visible to your 1:1 participants, as well as our Wiser Care
                    Team.
                </p>

                <WarningMessage message={error || getErrorMessages(apiError?.response?.data)?.join("")} />
                <div className="mx-auto flex justify-center mt-[50px] mb-3">
                    <Button className="!bg-accentOld" onClick={onConfirmClick}>
                        Confirm & Save
                    </Button>
                </div>

                <p className="text-center color-gray fs-14 mb-[40px]">
                    <span className="text-accentOld font-extrabold">
                        <Link to={CoachCalendarStepUrls.AVAILABILITY_STEP}>Back to Edit</Link>
                    </span>
                </p>

                {isLoading ? (
                    <FullSpinner />
                ) : (
                    <LazyCronofyWidget widget={CRONOFY_WIDGET.AvailabilityViewer} id="av-viewer" options={config} />
                )}
            </div>
        </SimpleLayout>
    )
}

export default ConfirmAvailabilityStep
